import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClientTransport } from '../handlers/ClientTransportHandler';
import { getClientViewHandler } from '../handlers/ClientViewHandler';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { getWebSocketUrl } from '../config/serverConfig';
import { useMaestroStore } from '../store/maestroStore';
import { isBridgeAvailable, getBridgeMode } from '../lib/playback-bridge';
import { TransportCommand } from '../transport/TransportCommand';
import { loadArtist, loadPerson, loadAlbum } from '../runtime/entityLoaders';

const SYNC_NAV_BLOCKED_PREFIXES = [
  '/app/devices',
  '/app/prism',
  '/app/settings',
  '/app/admin',
  '/app/control-center',
  '/app/support',
  '/onboarding',
];

function isOnUserOwnedRoute() {
  const path = window.location.pathname;
  return SYNC_NAV_BLOCKED_PREFIXES.some(prefix => path.startsWith(prefix));
}

/** Never navigate to /app/album/local:track:… — album pages need album keys, not track ids. */
function resolveSyncNavigationAlbumId(track) {
  if (!track) return null;
  const candidates = [
    track.albumId,
    track.album_id,
    track.album_key,
    track.albumKey,
  ].filter(Boolean);
  for (const c of candidates) {
    const s = String(c);
    if (/^local:track:/i.test(s)) continue;
    return s;
  }
  const id = track.id ? String(track.id) : '';
  if (/^local:album:/i.test(id)) return id;
  if (/^local:track:/i.test(id)) return null;
  return id || null;
}

let _localNavOverrideUntil = 0;

/**
 * Suppress incoming sync navigation for `durationMs` after a local user navigation.
 * Prevents the sync loop where Device A follows Device B back to a synced view
 * immediately after the user tried to navigate away.
 */
export function setLocalNavigationOverride(durationMs = 5000) {
  _localNavOverrideUntil = Date.now() + durationMs;
}

function isLocalNavigationOverrideActive() {
  return Date.now() < _localNavOverrideUntil;
}

export default function useBatonConnection() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const isScrubbingRef = useRef(false);

  useEffect(() => {
    const clientTransport = getClientTransport();
    const viewHandler = getClientViewHandler();

    const syncHandler = getSyncHandler();

    const connectToServer = async () => {
      try {
        const wsUrl = getWebSocketUrl();
        const hasToken = wsUrl.includes('token=');
        console.log(`[Baton] Connecting... token=${hasToken ? 'present' : 'MISSING'}`);
        if (!hasToken) {
          console.warn('[Baton] No auth token in WebSocket URL — server will likely reject');
        }
        await clientTransport.connect(wsUrl);
        console.log('[Baton] Connected to server, all handlers ready');
        // ClientSyncHandler binds in ClientTransportHandler WebSocket onopen (every connect + reconnect).
      } catch (error) {
        console.error('[Baton] Connection failed:', error);
      }
    };

    connectToServer();

    // ------------------------------------------------------------------
    // Transport events → write directly to Zustand store
    // ------------------------------------------------------------------
    const unsubConnection = clientTransport.onConnectionChanged((state) => {
      useMaestroStore.getState().setConnectionState(
        state === 'connected' ? 'connected' : 'disconnected'
      );
    });

    const unsubPlayback = clientTransport.onPlaybackStateChanged((state) => {
      const s = useMaestroStore.getState();
      const connectorOwns = isBridgeAvailable()
        && (getBridgeMode() === 'connector' || getBridgeMode() === 'ios-native')
        && (!!s.activeConnectorRenderer || !!s.rendererHasStream);
      if (connectorOwns) return;
      if (
        state === 'paused'
        && (s.playbackState === 'playing' || s.playbackState === 'loading')
        && s.rendererHasStream
      ) {
        console.log('[Baton] Skipping WS paused — renderer stream still active');
        return;
      }
      if (
        state === 'paused'
        && (s.playbackState === 'playing' || s.playbackState === 'loading')
        && TransportCommand.isInPlayUriPostSuccessSilenceWindow()
      ) {
        console.log('[Baton] Skipping WS paused — within playUri post-success window (rendererHasStream race)');
        return;
      }
      useMaestroStore.getState().setPlaybackState(state);
    });

    const unsubPosition = clientTransport.onPositionChanged((pos, dur) => {
      if (isScrubbingRef.current) return;
      const connectorOwns = !!useMaestroStore.getState().activeConnectorRenderer;
      if (connectorOwns) return;
      useMaestroStore.getState().updateTime(pos, dur);
    });

    const unsubTrack = clientTransport.onTrackChanged((track) => {
      const connectorOwns = !!useMaestroStore.getState().activeConnectorRenderer;
      if (connectorOwns) return;
      const store = useMaestroStore.getState();
      store.setCurrentTrack(track);
      if (track?.duration) store.setDuration(track.duration);
    });

    const unsubVolume = clientTransport.onVolumeChanged((level, muted) => {
      const connectorOwns = !!useMaestroStore.getState().activeConnectorRenderer;
      if (connectorOwns) return;
      const store = useMaestroStore.getState();
      store.setVolume(level);
      if (muted !== undefined) store.setMuted(muted);
    });

    // ------------------------------------------------------------------
    // View-sync subscriptions (cross-device navigation via BATON)
    // ------------------------------------------------------------------
    const unsubViewChanged = viewHandler.onViewChanged((screen, params) => {
      console.log(`[Baton] Remote navigation: ${screen}`, params);
      if (isLocalNavigationOverrideActive()) {
        console.log('[Baton] Skipping remote navigation — local user navigated recently');
        return;
      }
      if (isOnUserOwnedRoute()) {
        console.log('[Baton] Skipping remote navigation — user is on', window.location.pathname);
        return;
      }
      switch (screen) {
        case 'grid':
        case 'home':
          navigateRef.current('/app');
          break;
        case 'album_detail':
          if (params?.albumId) navigateRef.current(`/app/album/${encodeURIComponent(String(params.albumId))}`);
          break;
        case 'playlist_detail':
          if (params?.playlistId) navigateRef.current(`/app/playlist/${encodeURIComponent(String(params.playlistId))}`);
          break;
        case 'artist_detail':
          if (params?.artistId || params?.artistName) {
            const key = params.artistId || params.artistName || 'unknown';
            navigateRef.current(`/app/artist/${encodeURIComponent(String(key))}`);
            loadArtist({
              artistId: params.artistId || null,
              artistName: params.artistName || null,
              triggeredBy: 'baton',
            }).catch(() => {});
          }
          break;
        case 'person_detail':
          if (params?.personId) {
            navigateRef.current(`/app/person/${encodeURIComponent(String(params.personId))}`);
            loadPerson({
              personId: params.personId,
              personName: params.personName || null,
              triggeredBy: 'baton',
            }).catch(() => {});
          }
          break;
        case 'settings':
          navigateRef.current('/app/settings');
          break;
        case 'live_radio':
          navigateRef.current('/app/radio');
          break;
        case 'ella_radio':
        case 'maestro_radio':
          navigateRef.current('/app/ella-radio');
          break;
        case 'octave_radio':
          navigateRef.current('/app/octave-radio');
          break;
        case 'qobuz_browse':
          navigateRef.current('/app/qobuz');
          break;
        case 'favorites':
          navigateRef.current('/app/favorites');
          break;
        default:
          break;
      }
    });

    const unsubSelectionChanged = viewHandler.onSelectionChanged(
      (itemType, index, context) => {
        console.log(`[Baton] Remote selection: ${itemType} index=${index}`);
      }
    );

    const unsubZoneChanged = viewHandler.onZoneChanged((zoneName) => {
      console.log(`[Baton] Remote zone change: ${zoneName}`);
      useMaestroStore.getState().setZoneName(zoneName);
    });

    let hasNavigatedForSync = false;

    function tryNavigateToSyncAlbum() {
      if (hasNavigatedForSync) return;
      if (isLocalNavigationOverrideActive()) return;

      const session = syncHandler.getRemoteSession();
      let track = session?.track || null;
      let source = session?.activeSource || 'library';

      if (!track) {
        const ms = useMaestroStore.getState();
        const ct = ms.currentTrack;
        if (ct && ct.id) {
          const isRemote = syncHandler.isObserving()
            || (session && session.controllerId && session.controllerId !== syncHandler.getDeviceId());
          if (isRemote) {
            track = {
              id: ct.id || '',
              title: ct.title || '',
              artist: ct.artist || '',
              album: ct.album || '',
              artUrl: ct.artworkUrl || ct.artUrl || '',
              albumId: ct.albumId || '',
              source: ct.source || source,
            };
          }
        }
      }

      if (!track) return;

      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/app/album/')) return;
      if (isOnUserOwnedRoute()) {
        console.log('[Baton] Skipping sync navigation — user is on', currentPath);
        return;
      }

      const albumId = resolveSyncNavigationAlbumId(track);
      if (!albumId) return;

      hasNavigatedForSync = true;
      console.log('[Baton] Navigating to synced album:', track.album || track.title, 'id:', albumId);

      try {
        navigateRef.current(`/app/album/${encodeURIComponent(albumId)}`, {
          state: { source: track.source || source },
        });
      } catch (navErr) {
        console.error('[Baton] navigate() failed:', navErr);
      }

      loadAlbum({
        albumId,
        prefill: {
          id: albumId,
          title: track.album || track.title || '',
          artistName: track.artist || '',
          artworkUrl: track.artUrl || track.artworkUrl || '',
          source: track.source || source || 'library',
          serviceAlbumId: albumId,
        },
        triggeredBy: 'baton',
      }).catch(() => {});
    }

    const unsubObserver = syncHandler.onObserverStateChanged(tryNavigateToSyncAlbum);

    const unsubStoreSync = useMaestroStore.subscribe((state, prev) => {
      if (hasNavigatedForSync) return;
      if (!state.currentTrack || state.currentTrack.id === prev?.currentTrack?.id) return;
      setTimeout(tryNavigateToSyncAlbum, 50);
    });

    const syncPollInterval = setInterval(tryNavigateToSyncAlbum, 500);
    const syncPollTimeout = setTimeout(() => clearInterval(syncPollInterval), 15000);

    return () => {
      unsubConnection();
      unsubPlayback();
      unsubPosition();
      unsubTrack();
      unsubVolume();
      unsubViewChanged();
      unsubSelectionChanged();
      unsubZoneChanged();
      unsubObserver();
      unsubStoreSync();
      clearInterval(syncPollInterval);
      clearTimeout(syncPollTimeout);
      syncHandler.disconnect();
      clientTransport.disconnect();
    };
    // Intentionally empty deps: register BATON ↔ store wiring once per layout mount.
    // navigate is read via navigateRef so React Router upgrades do not re-subscribe transport.
  }, []);

  return { isScrubbingRef };
}
